import { CACHE_TTL_IN_MS } from 'authConfig'

/**
 * Stores the roles and permissions in local storage for the given account
 * @param {AccountInfo} account
 * @param {Array} roles
 * @param {Array} permissions
 */
export const setRolesAndPermissionsInStorage = (
  account,
  roles,
  permissions
) => {
  const newEntry = {
    roles: roles,
    permissions: permissions,
    lastAccessed: Date.now(),
    expiresOn: Date.now() + CACHE_TTL_IN_MS,
    sourceTokenId: account.idTokenClaims['uti'],
  }

  localStorage.setItem(
    `gmc.${account.idTokenClaims.aud}.${account.idTokenClaims.oid}`,
    JSON.stringify(newEntry)
  )
}

/**
 * Checks if the roles and permissions are in local storage and if their associated ID token is not expired
 * @param {AccountInfo} account
 * @returns {boolean}
 */
export const checkRolesAndPermissionsInStorage = account => {
  if (!account) return false

  const storageEntry = localStorage.getItem(
    `gmc.${account.idTokenClaims.aud}.${account.idTokenClaims.oid}`
  )

  if (!storageEntry) return false

  const parsedStorageEntry = JSON.parse(storageEntry)
  return (
    parsedStorageEntry.roles &&
    parsedStorageEntry.permissions &&
    parsedStorageEntry.expiresOn >= Date.now() &&
    parsedStorageEntry.sourceTokenId === account.idTokenClaims['uti']
  )
}

/**
 * Returns the roles array from local storage
 * @param {AccountInfo} account
 * @returns {Array}
 */
export const getRolesFromStorage = account => {
  if (!account) return null

  const storageEntry = localStorage.getItem(
    `gmc.${account.idTokenClaims.aud}.${account.idTokenClaims.oid}`
  )

  if (!storageEntry) return null

  return JSON.parse(storageEntry).roles
}

/**
 * Returns the permissions array from session storage
 * @param {AccountInfo} account
 * @returns {Array}
 */
export const getPermissionsFromStorage = account => {
  if (!account) return null

  const storageEntry = localStorage.getItem(
    `gmc.${account.idTokenClaims.aud}.${account.idTokenClaims.oid}`
  )

  if (!storageEntry) return null

  return JSON.parse(storageEntry).permissions
}

/**
 * This method clears session storage of roles and permissions claims for the given account.
 * @param {AccountInfo} account
 */
export const clearRolesAndPermissionsInStorage = account => {
  if (!account) return
  for (var key in localStorage) {
    if (
      key.startsWith(
        `gmc.${account.idTokenClaims.aud}.${account.idTokenClaims.oid}`
      )
    ) {
      localStorage.removeItem(key)
    }
  }
}
